import type { GetMeetingsParams } from '@api/types';
import { stableFilters } from './_filters';

type Id = string | number;

/**
 * Meeting query keys (Server-state rebuild, P4). Preserves today's exact values — the
 * domain legitimately spans three roots and the socket layer depends on them:
 *   - `['meetings', …]`      — the audit's meetings LIST (and the bare `['meetings']` root
 *                              that invalidates every audit's list).
 *   - `['meetings-all', id]` — the separate "all meetings" endpoint (`getAllMeetings`).
 *   - `['meeting', id, …]`   — a single meeting's DETAIL.
 * The new `details(auditId)` prefix helper names what used to be an inline
 * `['meeting', auditId]` array (closing the documents lint-guard gap from P3).
 */
/**
 * Root segment exported so the socket backlinks predicate (`useSyncAuditData`) can
 * compare by root, not by a bare string.
 */
export const MEETING_BACKLINKS_ROOT = 'meeting-backlinks';

export const meetingKeys = {
  // ── list root ['meetings'] ──────────────────────────────────────────────
  listRoot: () => ['meetings'] as const,
  lists: (auditId?: string) => ['meetings', auditId] as const,
  list: (auditId?: string, params?: GetMeetingsParams) => {
    const filters = stableFilters(params);
    return filters ? (['meetings', auditId, filters] as const) : (['meetings', auditId] as const);
  },

  // ── separate getAllMeetings endpoint ────────────────────────────────────
  allForAudit: (auditId?: string) => ['meetings-all', auditId] as const,

  // ── detail root ['meeting', …] ──────────────────────────────────────────
  details: (auditId?: string) => ['meeting', auditId] as const,
  detail: (auditId?: string, meetingId?: Id) => ['meeting', auditId, String(meetingId)] as const,

  // ── backlinks (own root, deliberate) ────────────────────────────────────
  /**
   * `GET /meetings/{id}/backlinks` (0.54.0) — the places that cite this meeting. Kept
   * OFF the detail root so the frequent detail/list invalidation after every meeting
   * edit never refetches the cross-entity reference scan; it refreshes on a sync of
   * another entity (`invalidateBacklinks`) and after a mention write.
   */
  backlinksRoot: () => [MEETING_BACKLINKS_ROOT] as const,
  backlinks: (auditId?: string, meetingId?: Id) =>
    [MEETING_BACKLINKS_ROOT, auditId, String(meetingId)] as const,
} as const;
